import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import Dropzone from "react-dropzone";
import { Editor } from "@tinymce/tinymce-react";
import { Button, Form, Card, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

import {
  SingleImageDropzone,
  MultipleImageDropzone,
} from "../DropzoneComponents";

const buildTreeSelectOptions = (items, level = 0) => {
  return items.flatMap((item) => {
    const indent = level > 0 ? "\u00A0".repeat(level * 4) : "";
    const prefix = level > 0 ? `${indent}↳ ` : "";

    const option = {
      value: item.value,
      label: prefix + item.label,
      brand_option: item.brand_option,
    };

    const children = item.children
      ? buildTreeSelectOptions(item.children, level + 1)
      : [];

    return [option, ...children];
  });
};

const ProductCreate = () => {
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE_URL;
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [features, setFeatures] = useState([]);
  const [showBrand, setShowBrand] = useState(false);
  const [formResetKey, setFormResetKey] = useState(Date.now());
  const inFlightRef = useRef(false);
  const [currencies, setCurrencies] = useState([]);
  const [errors, setErrors] = useState({});
  const location = useLocation();
  const copyProduct = location.state?.copyProduct;

  const [formData, setFormData] = useState({
    title: "",
    category_ids: [],
    brand_id: "",
    price: "",
    discount_price: "",
    barcode: "",
    stock: {},
    main_image: null,
    gallery: [],
    short_description: "",
    content: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    status: "aktif",
    features: {},
    currency_id: "",
  });
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/stock-categories/tree`).then((res) => {
      const flatTree = buildTreeSelectOptions(res.data);
      setCategories(flatTree);
    });

    axios
      .get(`${API}/api/stock-brands/active`)
      .then((res) => setBrands(res.data));
    axios
      .get(`${API}/api/warehouses/active`)
      .then((res) => setWarehouses(res.data));
    axios
      .get(`${API}/api/currencies/active`)
      .then((res) => setCurrencies(res.data));
  }, []);

  useEffect(() => {
    if (currencies.length > 0) {
      const def = currencies.find((c) => c.is_default === 1);
      if (def) {
        setFormData((prev) => ({
          ...prev,
          currency_id: def.id,
        }));
      }
    }
  }, [currencies]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");

    axios
      .get(`${API}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setSettings(res.data);
      })
      .catch(() => toast.error(getText("SETTINGS_FETCH_ERROR")));
  }, []);

  const resetFormButKeepCategories = () => {
    setFormResetKey(Date.now());
    setFormData((prev) => ({
      ...prev,
      title: "",
      brand_id: "",
      price: "",
      discount_price: "",
      barcode: "",
      stock: {},
      main_image: null,
      gallery: [],
      short_description: "",
      content: "",
      seo_title: "",
      seo_description: "",
      seo_keywords: "",
      status: "aktif",
      features: {},
      currency_id: "",
    }));

    try {
      const editor = window.tinymce?.get("productEditor");
      if (editor) editor.setContent("");
    } catch (e) {
      console.warn("Editor reset skipped", e);
    }

    //Step 2'de kal
    setStep(2);
  };

  const random5 = () => Math.floor(10000 + Math.random() * 90000).toString();

  // Ana kategori mi?
  const isMainCategory = (cat) => !cat.label.includes("↳");

  // Ana kategori index bul (1,2,3...)
  const getMainCategoryIndex = (categories, selectedCat) => {
    const mains = categories.filter(isMainCategory);
    const index = mains.findIndex((c) => c.value === selectedCat.value);
    return index >= 0 ? index + 1 : 1;
  };

  // Alt kategori sıra numarası (01,02...)
  const getSubCategoryIndex = (categories, selectedCat) => {
    const parentLabel = selectedCat.label.split("↳")[0].trim();

    const siblings = categories.filter(
      (c) => c.label.startsWith(parentLabel) && c.label.includes("↳"),
    );

    const index = siblings.findIndex((c) => c.value === selectedCat.value);
    return String(index + 1).padStart(2, "0");
  };

  const handleCategoryContinue = async () => {
    if (!formData.category_ids.length)
      return toast.error(getText("CATEGORY_REQUIRED_ERROR"));

    // 🔥 Barkod sadece ilk kategoriye göre üretilecek
    const selectedCat = formData.category_ids[0];

    let barcode = "";

    if (isMainCategory(selectedCat)) {
      // ANA KATEGORİ
      const mainIndex = getMainCategoryIndex(categories, selectedCat);
      barcode = `${mainIndex}00${random5()}`;
    } else {
      // ALT KATEGORİ
      const mainIndex = getMainCategoryIndex(categories, selectedCat);
      const subIndex = getSubCategoryIndex(categories, selectedCat);
      barcode = `${mainIndex}${subIndex}${random5()}`;
    }

    // 🔥 formData’ya yaz
    setFormData((prev) => ({
      ...prev,
      barcode,
    }));

    // Brand + feature logic (mevcut kodun)
    const ids = formData.category_ids.map((x) => x.value);
    const selectedCats = categories.filter((cat) => ids.includes(cat.value));
    const hasBrand = selectedCats.some((cat) => cat.brand_option === "evet");
    setShowBrand(hasBrand);

    const res = await axios.post(`${API}/api/features/by-categories`, {
      category_ids: ids,
    });
    setFeatures(res.data);
    setStep(2);
  };

  const handleFeatureChange = (featureId, values) => {
    // tekli seçim -> object
    if (!Array.isArray(values)) {
      setFormData({
        ...formData,
        features: {
          ...formData.features,
          [featureId]: values ? [values.value] : [],
        },
      });
      return;
    }

    // multi seçim -> dizi
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [featureId]: values.map((v) => v.value),
      },
    });
  };

  const onlyNumbers = (value) => value.replace(/[^0-9,.]/g, "");
  const resetFormWithCopy = () => {
    setFormResetKey(Date.now());

    // 🔥 kategoriye göre barkod üret
    let newBarcode = "";

    if (formData.category_ids.length > 0) {
      const selectedCat = formData.category_ids[0];

      if (isMainCategory(selectedCat)) {
        const mainIndex = getMainCategoryIndex(categories, selectedCat);
        newBarcode = `${mainIndex}00${random5()}`;
      } else {
        const mainIndex = getMainCategoryIndex(categories, selectedCat);
        const subIndex = getSubCategoryIndex(categories, selectedCat);
        newBarcode = `${mainIndex}${subIndex}${random5()}`;
      }
    }

    setFormData((prev) => ({
      ...prev,

      // ❌ sıfırlananlar
      title: "",
      barcode: newBarcode, // ✅ ARTIK BOŞ DEĞİL
      main_image: null,
      gallery: [],

      // ✅ kopyalananlar
      category_ids: prev.category_ids,
      brand_id: prev.brand_id,
      price: prev.price,
      discount_price: prev.discount_price,
      currency_id: prev.currency_id,
      stock: prev.stock,
      features: prev.features,
      short_description: prev.short_description,
      content: prev.content,
      seo_title: prev.seo_title,
      seo_description: prev.seo_description,
      seo_keywords: prev.seo_keywords,

      status: "aktif",
    }));

    // TinyMCE içeriğini geri yaz
    setTimeout(() => {
      const editor = window.tinymce?.get("productEditor");
      if (editor) {
        editor.setContent(formData.content || "");
      }
    }, 0);

    setStep(2);
  };

  useEffect(() => {
    if (!copyProduct || categories.length === 0) return;

    const selectedCategories = categories.filter((c) =>
      copyProduct.categories?.includes(c.value),
    );

    setFormData((prev) => ({
      ...prev,
      barcode: "", // önce temizle
      title: copyProduct.title || "",
      price: copyProduct.price || "",
      discount_price: copyProduct.discount_price || "",
      brand_id: copyProduct.brand_id || "",
      currency_id: copyProduct.currency_id || "",
      stock: copyProduct.stock || {},
      short_description: copyProduct.short_description || "",
      content: copyProduct.content || "",
      seo_title: copyProduct.seo_title || "",
      seo_description: copyProduct.seo_description || "",
      seo_keywords: copyProduct.seo_keywords || "",
      features: copyProduct.features || {},
      status: "aktif",
      category_ids: selectedCategories,
    }));

    setShowBrand(Boolean(copyProduct.brand_id));

    // 🔥 BARKOD ÜRET
    if (selectedCategories.length > 0) {
      const selectedCat = selectedCategories[0];
      let barcode = "";

      if (isMainCategory(selectedCat)) {
        const mainIndex = getMainCategoryIndex(categories, selectedCat);
        barcode = `${mainIndex}00${random5()}`;
      } else {
        const mainIndex = getMainCategoryIndex(categories, selectedCat);
        const subIndex = getSubCategoryIndex(categories, selectedCat);
        barcode = `${mainIndex}${subIndex}${random5()}`;
      }

      setFormData((prev) => ({
        ...prev,
        barcode,
      }));
    }

    setTimeout(() => {
      const editor = window.tinymce?.get("productEditor");
      if (editor) {
        editor.setContent(copyProduct.content || "");
      }
    }, 200);

    setStep(2);
  }, [copyProduct, categories]);

  const handleSubmit = async (e, nextMode = "back") => {
    if (e) e.preventDefault();

    // Çift çağrıyı engelle
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    if (!validateForm()) {
      inFlightRef.current = false;
      setLoading(false);
      return;
    }

    try {
      // ---- Zorunlu özellik kontrolü ----
      for (const f of features) {
        if (
          f.is_required &&
          (!formData.features[f.id] || formData.features[f.id].length === 0)
        ) {
          toast.error(`${getText("REQUIRED_FEATURE_ERROR")} ${f.title}`);
          throw new Error("required feature missing");
        }
      }

      const cleanedStock = { ...formData.stock };
      Object.keys(cleanedStock).forEach((k) => {
        if (!cleanedStock[k]) delete cleanedStock[k];
      });

      const payload = new FormData();
      const body = {
        ...formData,
        categories: formData.category_ids.map((x) => x.value),
        stock: cleanedStock,
      };

      Object.entries(body).forEach(([key, value]) => {
        if (key !== "main_image" && key !== "gallery") {
          payload.append(
            key,
            typeof value === "object" ? JSON.stringify(value) : value,
          );
        }
      });

      if (formData.main_image)
        payload.append("main_image", formData.main_image);
      formData.gallery.forEach((file) => payload.append("gallery", file));

      const res = await axios.post(`${API}/api/products`, payload);
      console.log("API Success:", res.data);
      toast.success(getText("PRODUCT_SAVED"));

      if (nextMode === "new") {
        resetFormButKeepCategories();
        // Kilidi sadece burada yumuşak bırak
        setTimeout(() => {
          inFlightRef.current = false;
        }, 300);
        setLoading(false);
        return;
      }
      if (nextMode === "copy") {
       toast.success(getText("PRODUCT_COPIED_READY"));
        resetFormWithCopy();

        setTimeout(() => {
          inFlightRef.current = false;
        }, 300);

        setLoading(false);
        return;
      }

      navigate("/products/table");
    } catch (err) {
      console.log("API Error:", err);
      toast.error(err?.response?.data?.message || getText("PRODUCT_SAVE_FAILED"));
      // catch içinde kilidi bırak
      inFlightRef.current = false;
      setLoading(false);
    }
  };
  const validateForm = () => {
    const newErrors = {};

    if (!formData.price?.trim()) {
      newErrors.price = getText("FIELD_REQUIRED");
    }

    if (!formData.currency_id) {
      newErrors.currency_id = getText("FIELD_REQUIRED");
    }
    if (!formData.main_image) {
      newErrors.main_image = getText("FIELD_REQUIRED");
    }
    if (!formData.title) {
      newErrors.title = getText("FIELD_REQUIRED");
    }

    // Marka zorunluysa
    if (showBrand && !formData.brand_id) {
      newErrors.brand_id = getText("FIELD_REQUIRED");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0; // true → geçerli
  };

  return (
    <div className="container mt-4">
      {/* STEP 1: CATEGORY */}
      {step === 1 && (
        <Card className="shadow-sm p-4">
          <h4 className="mb-3">{getText("CATEGORY_SELECT_TITLE")}</h4>

          <Form.Label>{getText("CATEGORIES_LABEL")}</Form.Label>
          <Select
            isMulti
            options={categories}
            value={formData.category_ids}
            onChange={(v) => setFormData({ ...formData, category_ids: v })}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />

          <Button className="mt-3 w-100" onClick={handleCategoryContinue}>
            {getText("CONTINUE_BUTTON")}
          </Button>
        </Card>
      )}

      {/* STEP 2 FORM */}
      {step === 2 && (
        <div key={formResetKey}>
          <Button className="mb-3" size="sm" onClick={() => setStep(1)}>
         {getText("CHANGE_CATEGORY_BUTTON")}
          </Button>
          {/* Seçili Kategoriler Gösterimi */}
          <div className="mb-3 p-2 bg-light border rounded">
            <strong>{getText("SELECTED_CATEGORIES_LABEL")}:</strong>
            <div className="mt-2">
              {formData.category_ids.map((c) => (
                <span
                  key={c.value}
                  className="badge bg-primary me-2 mb-2"
                  style={{ fontSize: "14px", padding: "6px 10px" }}
                >
                  {c.label}
                </span>
              ))}
            </div>
          </div>

          {/* CARD 1: ÜRÜN BİLGİLERİ */}
          <Card className="shadow-sm p-4 mb-4">
            <h5 className="mb-3">{getText("PRODUCT_INFORMATION_TITLE")}</h5>

            <Row className="mb-3 g-3 align-items-end">
              {showBrand && (
                <Col xs={12} md={3}>
                  <Form.Label>
                    {getText("BRAND_LABEL")}<span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={brands.map((b) => ({
                      value: b.id,
                      label: b.title,
                    }))}
                    onChange={(v) =>
                      setFormData({ ...formData, brand_id: v?.value || "" })
                    }
                    value={
                      formData.brand_id
                        ? brands
                            .filter((b) => b.id === formData.brand_id)
                            .map((b) => ({ value: b.id, label: b.title }))
                        : null
                    }
                  />
                  {errors.brand_id && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {errors.brand_id}
                    </div>
                  )}
                </Col>
              )}

              <Col xs={12} md={showBrand ? 3 : 3}>
                <Form.Label>
                  {getText("BARCODE_LABEL")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control value={formData.barcode} disabled readOnly />

                {errors.barcode && (
                  <div
                    className="text-danger mt-1"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {errors.barcode}
                  </div>
                )}
              </Col>

              <Col xs={12} md={showBrand ? 2 : 3}>
                <Form.Label>
                  {getText("PRICE_LABEL")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      price: onlyNumbers(e.target.value),
                    })
                  }
                />
                {errors.price && (
                  <div
                    className="text-danger mt-1"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {errors.price}
                  </div>
                )}
              </Col>

              <Col xs={12} md={showBrand ? 2 : 3}>
                <Form.Label>{getText("DISCOUNT_PRICE_LABEL")}</Form.Label>
                <Form.Control
                  value={formData.discount_price}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      discount_price: onlyNumbers(e.target.value),
                    })
                  }
                />
              </Col>

              <Col xs={12} md={showBrand ? 2 : 3}>
                <Form.Label>
                  {getText("CURRENCY_LABEL")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={formData.currency_id || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, currency_id: e.target.value })
                  }
                >
                  <option value="">{getText("SELECT_PLACEHOLDER")}</option>
                  {currencies?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code}
                    </option>
                  ))}
                </Form.Select>
                {errors.currency_id && (
                  <div
                    className="text-danger mt-1"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {errors.currency_id}
                  </div>
                )}
              </Col>
            </Row>

            <h6>{getText("WAREHOUSE_STOCK_TITLE")}</h6>
            {warehouses.map((w) => (
              <div key={w.id} className="mb-2">
                <small className="fw-bold">{w.title}</small>

                <Form.Control
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Sayısı girilmezse sınırsız olur"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "");
                  }}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stock: {
                        ...formData.stock,
                        [w.id]: onlyNumbers(e.target.value),
                      },
                    })
                  }
                />
              </div>
            ))}
          </Card>

          {/* CARD 2: AÇIKLAMALAR + EDITOR */}
          <Card className="shadow-sm p-4 mb-4">
            <h5 className="mb-2">{getText("PRODUCT_DESCRIPTIONS_TITLE")}</h5>

            <Form.Label>
              {getText("PRODUCT_TITLE_LABEL")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              className="mb-3"
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
            {errors.title && (
              <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
                {errors.title}
              </div>
            )}

            <Form.Label>
             {getText("LIST_IMAGE_LABEL")} ({settings?.product_list_image_width}×
              {settings?.product_list_image_height}){" "}
              <span className="text-danger">*</span>
            </Form.Label>

            {settings ? (
              <SingleImageDropzone
                fieldName="main_image"
                fieldValue={formData.main_image}
                setFieldValues={(updater) => {
                  const newFile =
                    typeof updater === "function"
                      ? updater({ main_image: formData.main_image }).main_image
                      : updater.main_image;
                  setFormData({ ...formData, main_image: newFile });
                }}
                required={true}
                maxWidth={Number(settings.product_list_image_width)}
                maxHeight={Number(settings.product_list_image_height)}
              />
            ) : (
              <p>Ayarlar yükleniyor...</p>
            )}
            {errors.main_image && (
              <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
                {errors.main_image}
              </div>
            )}
            <Form.Label>
             {getText("PRODUCT_GALLERY_LABEL")} ({settings?.product_gallery_image_width}×
              {settings?.product_gallery_image_height})
            </Form.Label>

            {settings ? (
              <MultipleImageDropzone
                fieldName="gallery"
                fieldValue={formData.gallery}
                setFieldValues={(updater) => {
                  const newFiles =
                    typeof updater === "function"
                      ? updater({ gallery: formData.gallery }).gallery
                      : updater.gallery;
                  setFormData({ ...formData, gallery: newFiles });
                }}
                required={false}
                maxWidth={Number(settings.product_gallery_image_width)}
                maxHeight={Number(settings.product_gallery_image_height)}
              />
            ) : (
              <p>Ayarlar yükleniyor...</p>
            )}

            <Form.Label>{getText("SHORT_DESCRIPTION_LABEL")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="mb-3"
              value={formData.short_description || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  short_description: e.target.value,
                })
              }
            />

            <Form.Label>{getText("DETAILED_CONTENT_LABEL")}</Form.Label>
            <Editor
              key={formResetKey}
              id="productEditor"
              value={formData.content || ""}
              tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
              onEditorChange={(content) =>
                setFormData({ ...formData, content })
              }
              init={{
                promotion: false,
                height: 400,
                plugins: [
                  "advlist",
                  "autolink",
                  "link",
                  "image",
                  "lists",
                  "charmap",
                  "preview",
                  "anchor",
                  "pagebreak",
                  "searchreplace",
                  "wordcount",
                  "visualblocks",
                  "code",
                  "fullscreen",
                  "insertdatetime",
                  "media",
                  "table",
                  "emoticons",
                  "help",
                ],
                toolbar:
                  "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
                  "bullist numlist outdent indent | link image | " +
                  "print preview media fullscreen code | forecolor backcolor emoticons | help",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
                image_advtab: true,
                automatic_uploads: false,
                images_upload_url: `${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
                images_upload_handler: null,
              }}
            />
          </Card>

          <Card className="shadow-sm p-4 mb-4">
            {features.length > 0 && (
              <>
                <h5 className="mt-4">{getText("PRODUCT_FEATURES_TITLE")}</h5>

                {features.map((f) => (
                  <div key={f.id} className="mb-4 p-3 border rounded">
                    {/* Başlık + Seçimi Kaldır */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="fw-bold">
                        {f.title}{" "}
                        {f.is_required && (
                          <span style={{ color: "red" }}>*</span>
                        )}
                      </label>

                      {/* 🔥 Zorunlu olmayanlarda temizle butonu */}
                      {!f.is_required && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              features: { ...formData.features, [f.id]: [] },
                            })
                          }
                        >
                         {getText("CLEAR_SELECTION_BUTTON")}
                        </button>
                      )}
                    </div>

                    {/* SELECT MENU */}
                    {f.input_type === "select" ? (
                      <Select
                        isMulti={Boolean(f.is_multiple)}
                        options={f.values.map((v) => ({
                          value: v.id,
                          label: v.value,
                        }))}
                        placeholder={getText("SELECT_PLACEHOLDER")}
                        value={
                          formData.features[f.id]
                            ? f.values
                                .filter((v) =>
                                  formData.features[f.id].includes(v.id),
                                )
                                .map((v) => ({ value: v.id, label: v.value }))
                            : []
                        }
                        onChange={(v) => handleFeatureChange(f.id, v)}
                      />
                    ) : (
                      // CHECKBOX / RADIO
                      <div className="d-flex flex-wrap gap-3">
                        {f.values.map((v) => {
                          const selected = formData.features[f.id]?.includes(
                            v.id,
                          );
                          return (
                            <div key={v.id} className="form-check">
                              <input
                                type={f.is_multiple ? "checkbox" : "radio"}
                                name={`feature-${f.id}`}
                                className="form-check-input"
                                checked={selected}
                                onChange={() => {
                                  let newValues = [];

                                  if (f.is_multiple) {
                                    newValues = selected
                                      ? formData.features[f.id].filter(
                                          (x) => x !== v.id,
                                        )
                                      : [
                                          ...(formData.features[f.id] || []),
                                          v.id,
                                        ];
                                  } else {
                                    newValues = selected ? [] : [v.id];
                                  }

                                  setFormData({
                                    ...formData,
                                    features: {
                                      ...formData.features,
                                      [f.id]: newValues,
                                    },
                                  });
                                }}
                              />
                              <label className="form-check-label">
                                {v.value}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </Card>

          <Card className="shadow-sm p-4 mb-4">
            {/* SEO */}
            <h5 className="mt-4"> {getText("SEO_INFORMATION_TITLE")} </h5>
            <Form.Label>{getText("SEO_TITLE_LABEL")}</Form.Label>
            <Form.Control
              className="mb-2"
              placeholder="SEO Başlık"
              value={formData.seo_title || ""}
              onChange={(e) =>
                setFormData({ ...formData, seo_title: e.target.value })
              }
            />
            <Form.Label>{getText("SEO_DESCRIPTION_LABEL")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="mb-2"
              placeholder="SEO Açıklama"
              value={formData.seo_description || ""}
              onChange={(e) =>
                setFormData({ ...formData, seo_description: e.target.value })
              }
            />

            <Form.Label>{getText("SEO_KEYWORDS_LABEL")}</Form.Label>
            <Form.Control
              className="mb-2"
              placeholder="SEO Keywords"
              value={formData.seo_keywords || ""}
              onChange={(e) =>
                setFormData({ ...formData, seo_keywords: e.target.value })
              }
            />

            <Form.Label>{getText("STATUS_LABEL")}</Form.Label>

            <Form.Select
              className="mb-3"
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
            >
              <option value="aktif">{getText("STATUS_ACTIVE")}</option>
              <option value="pasif">{getText("STATUS_PASSIVE")}</option>
            </Form.Select>

            <div className="d-flex justify-content-center gap-2">
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, "back")}
                disabled={loading}
                variant="primary"
              >
                {getText("SAVE_BUTTON")}
              </Button>

              <Button
                type="button"
                onClick={(e) => handleSubmit(e, "new")}
                disabled={loading}
                variant="success"
              >
                {getText("SAVE_NEW_BUTTON")}
              </Button>
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, "copy")}
                disabled={loading}
                variant="warning"
              >
               {getText("SAVE_COPY_BUTTON")}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductCreate;
